import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Card, 
  Table, 
  Input, 
  Button, 
  message,
  Typography,
  Avatar,
  Tag
} from 'antd';
import { PlusOutlined, SearchOutlined, EditOutlined } from '@ant-design/icons';
import PageLayout from '../../../components/layout/PageLayout';
import tvShowService from '../../../services/tvShowService';
import { useUser } from '../../../context/UserContext';
import moment from 'moment';

const { Title, Text } = Typography;
const { Search } = Input;

const EpisodeList = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [episodes, setEpisodes] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} episodes`
  });
  
  // Filters
  const [filters, setFilters] = useState({
    search: ''
  });

  const getEpisodes = useCallback(async (params = {}) => {
    try {
      setLoading(true);
      
      const requestParams = {
        perPage: pagination.pageSize,
        currentPage: pagination.current,
        searchText: filters.search,
        ...params
      };
      
      console.log('Fetching episodes with params:', requestParams);
      const response = await tvShowService.getAllTvShowEpisode(requestParams);
      console.log('Episodes API Response:', response);
      
      if (response.data && response.data.status) {
        const episodesData = response.data.data?.data || [];
        const paginationData = response.data.data?.pagination || {};
        
        console.log('Episodes data:', episodesData);
        console.log('Pagination data:', paginationData);
        
        // Ensure episodesData is an array
        if (Array.isArray(episodesData)) {
          setEpisodes(episodesData);
          setPagination(prev => ({
            ...prev,
            total: paginationData.total || episodesData.length,
            current: paginationData.currentPage || prev.current
          }));
        } else {
          console.error('Episodes data is not an array:', episodesData);
          setEpisodes([]);
        }
      } else {
        message.error('Failed to fetch episodes');
        setEpisodes([]);
      }
    } catch (error) {
      console.error('Error fetching episodes:', error);
      message.error('Failed to fetch episodes');
      setEpisodes([]);
    } finally {
      setLoading(false);
    }
  }, [filters.search, pagination.pageSize, pagination.current]);

  // Load episodes data
  useEffect(() => {
    getEpisodes();
  }, [getEpisodes]);

  // Handle search
  const handleSearch = (value) => {
    setFilters(prev => ({ ...prev, search: value }));
    setPagination(prev => ({ ...prev, current: 1 }));
    getEpisodes({
      searchText: value,
      currentPage: 1
    });
  };

  // Handle table change (pagination, sorting, filtering)
  const handleTableChange = (paginationInfo, tableFilters, sorter) => {
    const { current, pageSize } = paginationInfo;
    
    setPagination(prev => ({
      ...prev,
      current,
      pageSize
    }));
    
    getEpisodes({
      currentPage: current,
      perPage: pageSize,
      searchText: filters.search
    });
  };

  // Handle add episode
  const handleAddEpisode = () => {
    navigate('/TvShows/episodes/addTvShow');
  };

  // Handle edit episode
  const handleEditEpisode = (episodeId) => {
    navigate(`/TvShows/episodes/editTvShow/${episodeId}`);
  };

  // Format date from timestamp
  const getDateFromTs = (timestamp) => {
    if (timestamp) {
      return moment.unix(timestamp).format('YYYY-MM-DD HH:mm:ss');
    }
    return 'No Date';
  };

  // Table columns
  const columns = [
    {
      title: 'Season',
      dataIndex: 'season_no',
      key: 'season_no',
      width: 80,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => (a.season_no || 0) - (b.season_no || 0),
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text || 'N/A'}
        </Text>
      )
    },
    {
      title: 'Episode',
      dataIndex: 'episode_no',
      key: 'episode_no',
      width: 80,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => (a.episode_no || 0) - (b.episode_no || 0),
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text || 'N/A'}
        </Text>
      )
    },
    {
      title: 'Title',
      dataIndex: 'episode_title',
      key: 'episode_title',
      defaultSortOrder: 'ascend',
      sorter: (a, b) => a.episode_title?.localeCompare(b.episode_title || ''),
      render: (text) => (
        <Text strong style={{ fontSize: '14px' }}>
          {text || 'N/A'}
        </Text>
      )
    },
    {
      title: 'Summary',
      dataIndex: 'episode_summary',
      key: 'episode_summary',
      width: 200,
      render: (text) => (
        <Text 
          style={{ fontSize: '13px' }}
          ellipsis={{ tooltip: text }}
        >
          {text || 'N/A'}
        </Text>
      )
    },
    {
      title: 'Run Time',
      dataIndex: 'episode_runtime',
      key: 'episode_runtime',
      width: 100,
      defaultSortOrder: 'ascend',
      sorter: (a, b) => (a.episode_runtime || 0) - (b.episode_runtime || 0),
      render: (text) => (
        <Text style={{ fontSize: '14px' }}>
          {text ? `${text} min` : 'N/A'}
        </Text>
      )
    },
    {
      title: 'Release Date',
      dataIndex: 'release_date',
      key: 'release_date',
      width: 150,
      defaultSortOrder: 'ascend',
      render: (text) => (
        <Text style={{ fontSize: '13px' }}>
          {getDateFromTs(text)}
        </Text>
      )
    },
    {
      title: 'Preview',
      dataIndex: 'available_for_preview',
      key: 'available_for_preview',
      width: 100,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Yes' : 'No'}
        </Tag>
      )
    },
    {
      title: 'Image 1',
      dataIndex: 'episode_image_1',
      key: 'episode_image_1',
      width: 100,
      render: (text) => (
        <Text style={{ fontSize: '12px' }}>
          {text ? 'Available' : 'N/A'}
        </Text>
      )
    },
    {
      title: 'Image 2',
      dataIndex: 'episode_image_2',
      key: 'episode_image_2',
      width: 100,
      render: (text) => (
        <Text style={{ fontSize: '12px' }}>
          {text ? 'Available' : 'N/A'}
        </Text>
      )
    },
    {
      title: 'Status',
      dataIndex: 'episode_is_active',
      key: 'episode_is_active',
      width: 100,
      render: (status) => (
        <Tag color={status === 'Y' ? 'green' : 'red'}>
          {status === 'Y' ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'action',
      width: 100,
      fixed: 'right',
      render: (text, record) => (
        <Button
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEditEpisode(record.episode_id)}
          size="small"
        >
          Edit
        </Button>
      )
    }
  ];

  // Header content with user info
  const headerContent = (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
        <Avatar 
          src={user?.avatar} 
          size={64} 
          style={{ marginRight: '16px' }}
        >
          {user?.name?.charAt(0)?.toUpperCase()}
        </Avatar>
        <div>
          <Title level={4} style={{ margin: 0, marginBottom: '4px' }}>
            {user?.name || 'Admin'}
          </Title>
          <Text type="secondary">Manage TV Show Episodes</Text>
        </div>
      </div>
    </div>
  );

  return (
    <PageLayout
      title="TV Show Episodes List"
      desc="Manage TV show episodes and their details"
      avatar={user?.avatar}
      headerContent={headerContent}
    >
      <Card>
        <div style={{ marginBottom: '16px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            marginBottom: '16px'
          }}>
            <Title level={4} style={{ margin: 0 }}>
              TV Show Episodes ({pagination.total})
            </Title>
            
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddEpisode}
              size="large"
            >
              Add Episode
            </Button>
          </div>
          
          <div style={{ 
            display: 'flex', 
            gap: '16px', 
            alignItems: 'center',
            flexWrap: 'wrap'
          }}>
            <Search
              placeholder="Search episodes..."
              allowClear
              onSearch={handleSearch}
              style={{ width: '50%', minWidth: '200px' }}
              enterButton={<SearchOutlined />}
            />
          </div>
        </div>

        <Table
          columns={columns}
          dataSource={episodes}
          rowKey="episode_id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1500 }}
          size="middle"
          locale={{ 
            emptyText: 'No episodes found',
            filterConfirm: 'OK',
            filterReset: 'Reset',
            filterEmptyText: 'No filters'
          }}
        />
      </Card>
    </PageLayout>
  );
};

export default EpisodeList;